import { Context, Data, Effect, Layer, SubscriptionRef } from 'effect';
import type { EffectSuccess } from '../../data/util/effectTypes';
import type { SupportBoxWindow } from './window';

import { ContextTag } from '../context';

class SupportBoxOperationError extends Data.TaggedError('SupportBoxOperationError')<{
  readonly operation: string;
  readonly cause: unknown;
}> {
  override get message(): string {
    return `Failed to ${this.operation} support box: ${String(this.cause)}`;
  }
}

export type SupportBoxState = {
  isHidden: boolean;
  isInitialized: boolean;
  isOnline: boolean;
  lastError: string | null;
};

const make = Effect.gen(function* () {
  const stateRef = yield* SubscriptionRef.make<SupportBoxState>({
    isHidden: false,
    isInitialized: false,
    isOnline: false,
    lastError: null,
  });

  const checkApiAvailable = (): boolean => {
    if (typeof window === 'undefined') {
      return false;
    }
    // Typing through SupportBoxWindow keeps ./window's global augmentation in
    // every consumer program instead of relying on an app-local declaration.
    const supportBoxWindow: SupportBoxWindow = window;
    return supportBoxWindow.supportBoxApi !== undefined;
  };

  // Update online status from window API
  const updateOnlineStatus = Effect.gen(function* () {
    if (!checkApiAvailable()) {
      return;
    }

    const result = yield* Effect.try({
      try: () => window.supportBoxApi?.isOnline() ?? false,
      catch: () => false,
    });

    yield* SubscriptionRef.update(stateRef, (state) => ({
      ...state,
      isOnline: result,
      isInitialized: true,
    }));
  });

  // Setup event listeners for real-time status updates (client-side only)
  const setupEventListeners =
    typeof window === 'undefined'
      ? Effect.void
      : Effect.acquireRelease(
          Effect.gen(function* () {
            const runtime = yield* Effect.context();
            const handleStatusChange = () => {
              Effect.runSyncWith(runtime)(updateOnlineStatus);
            };

            const handleApiInitialized = () => {
              Effect.runSyncWith(runtime)(updateOnlineStatus);
            };

            // Check initial state
            yield* updateOnlineStatus;

            // Listen for status changes
            window.addEventListener('supportBox:status:change', handleStatusChange);
            window.addEventListener('supportBox:api:initialized', handleApiInitialized);

            return { handleStatusChange, handleApiInitialized };
          }),

          ({ handleStatusChange, handleApiInitialized }) =>
            Effect.sync(() => {
              window.removeEventListener('supportBox:status:change', handleStatusChange);
              window.removeEventListener('supportBox:api:initialized', handleApiInitialized);
            })
        );

  // Start listening to events

  yield* Effect.forkScoped(setupEventListeners);

  const waitForApiInitialization = Effect.callback<void>((resume) => {
    if (typeof window === 'undefined') {
      resume(Effect.void);
      return;
    }

    if (checkApiAvailable()) {
      resume(Effect.void);
      return;
    }

    const handleInitialized = () => {
      resume(Effect.void);
    };

    window.addEventListener('supportBox:api:initialized', handleInitialized, { once: true });

    const timeout = setTimeout(() => {
      window.removeEventListener('supportBox:api:initialized', handleInitialized);
      resume(Effect.void);
    }, 5000);

    return Effect.sync(() => {
      clearTimeout(timeout);
      window.removeEventListener('supportBox:api:initialized', handleInitialized);
    });
  });

  const hide = Effect.gen(function* () {
    if (!checkApiAvailable()) {
      yield* waitForApiInitialization;
    }

    if (!checkApiAvailable()) {
      yield* SubscriptionRef.update(stateRef, (state) => ({
        ...state,
        lastError: 'Support Box API not available after initialization timeout',
      }));
      return false;
    }

    yield* Effect.try({
      try: () => {
        if (window.supportBoxApi) {
          window.supportBoxApi.hide();
        }
      },
      catch: (cause) => new SupportBoxOperationError({ operation: 'hide', cause }),
    }).pipe(
      Effect.catchTag('SupportBoxOperationError', (error) =>
        SubscriptionRef.update(stateRef, (state) => ({
          ...state,
          lastError: error.message,
        }))
      )
    );

    yield* SubscriptionRef.update(stateRef, (state) => ({
      ...state,
      isHidden: true,
      isInitialized: true,
      lastError: null,
    }));

    return true;
  });

  const show = Effect.gen(function* () {
    if (!checkApiAvailable()) {
      return false;
    }

    yield* Effect.try({
      try: () => {
        if (window.supportBoxApi) {
          window.supportBoxApi.show();
        }
      },
      catch: (cause) => new SupportBoxOperationError({ operation: 'show', cause }),
    }).pipe(
      Effect.catchTag('SupportBoxOperationError', (error) =>
        SubscriptionRef.update(stateRef, (state) => ({
          ...state,
          lastError: error.message,
        }))
      )
    );

    yield* SubscriptionRef.update(stateRef, (state) => ({
      ...state,
      isHidden: false,
      lastError: null,
    }));

    return true;
  });

  const open = Effect.gen(function* () {
    if (!checkApiAvailable()) {
      return false;
    }

    yield* Effect.try({
      try: () => {
        if (window.supportBoxApi) {
          window.supportBoxApi.open();
        }
      },
      catch: (cause) => new SupportBoxOperationError({ operation: 'open', cause }),
    }).pipe(
      Effect.catchTag('SupportBoxOperationError', (error) =>
        SubscriptionRef.update(stateRef, (state) => ({
          ...state,
          lastError: error.message,
        }))
      )
    );

    yield* SubscriptionRef.update(stateRef, (state) => ({
      ...state,
      lastError: null,
    }));

    return true;
  });

  const close = Effect.gen(function* () {
    if (!checkApiAvailable()) {
      return false;
    }

    yield* Effect.try({
      try: () => {
        if (window.supportBoxApi) {
          window.supportBoxApi.close();
        }
      },
      catch: (cause) => new SupportBoxOperationError({ operation: 'close', cause }),
    }).pipe(
      Effect.catchTag('SupportBoxOperationError', (error) =>
        SubscriptionRef.update(stateRef, (state) => ({
          ...state,
          lastError: error.message,
        }))
      )
    );

    yield* SubscriptionRef.update(stateRef, (state) => ({
      ...state,
      lastError: null,
    }));

    return true;
  });

  const getState = SubscriptionRef.get(stateRef);

  return {
    stateRef,
    hide,
    show,
    open,
    close,
    getState,
  } as const;
});

export class SupportBox extends Context.Service<SupportBox, EffectSuccess<typeof make>>()(
  ContextTag.SupportBox
) {
  static readonly Live = Layer.effect(this, make);
}
